import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, MapPin } from "lucide-react";
import { SiteLayout } from "./layout";
import { HeroImage } from "./hero-image";
import { Reveal } from "./reveal";
import { AREA_PAGES, SERVICE_PAGES, SITE } from "@/lib/site";

export function LocationPage({ slug }: { slug: string }) {
  const page = AREA_PAGES.find((p) => p.slug === slug)!;
  const otherAreas = AREA_PAGES.filter((p) => p.slug !== slug);
  const featuredServices = SERVICE_PAGES.filter((s) =>
    ["bilvask-drammen", "dekkskift-drammen", "oljeskift-drammen"].includes(s.slug),
  );

  return (
    <SiteLayout>
      <section className="relative min-h-[70svh] flex items-end overflow-hidden">
        <HeroImage />
        <div className="relative z-10 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-10 pb-16 sm:pb-24 pt-32">
          <Reveal>
            <div className="eyebrow text-primary/90">{SITE.name} · {page.distance}</div>
          </Reveal>
          <Reveal delay={140}>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[1] tracking-tight text-white mt-6 text-balance max-w-3xl">
              {page.h1}
            </h1>
          </Reveal>
          <Reveal delay={280}>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-white/85">{page.intro}</p>
          </Reveal>
          <Reveal delay={420}>
            <Link
              to="/book"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
            >
              Book time <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow">For deg i {page.area}</div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-4 text-balance">Derfor velger kunder fra {page.area} oss</h2>
          </Reveal>
          <ul className="mt-10 grid gap-4 sm:grid-cols-2">
            {page.points.map((p, i) => (
              <Reveal key={p} delay={i * 80}>
                <li className="flex gap-3 rounded-xl border border-border bg-card p-5 text-sm text-foreground/90">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {p}
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={200}>
            <div className="mt-10 flex items-start gap-3 rounded-xl border border-border bg-secondary/30 p-5 text-sm text-foreground/85">
              <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>Vi holder til på {SITE.address}, {page.distance}.</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="eyebrow">Våre tjenester</div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featuredServices.map((s) => (
              <Link
                key={s.slug}
                to={"/" + s.slug as any}
                className="rounded-xl border border-border bg-card p-5 hover:border-primary transition-colors flex items-center justify-between"
              >
                <span className="font-serif text-lg">{s.h1}</span>
                <ArrowRight className="h-4 w-4 text-primary" />
              </Link>
            ))}
          </div>
          {otherAreas.length > 0 && (
            <>
              <div className="eyebrow mt-12">Vi betjener også</div>
              <div className="mt-6 flex flex-wrap gap-3">
                {otherAreas.map((a) => (
                  <Link
                    key={a.slug}
                    to={"/" + a.slug as any}
                    className="rounded-full border border-border bg-card px-5 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors"
                  >
                    Bilvask i {a.area}
                  </Link>
                ))}
              </div>
            </>
          )}
          <div className="mt-10">
            <Link to="/" className="text-sm text-muted-foreground hover:text-primary">← Tilbake til forsiden</Link>
          </div>
        </div>
      </section>

      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 gradient-radial" />
        <div className="relative mx-auto max-w-2xl px-4 sm:px-6 lg:px-10 text-center">
          <h2 className="font-serif text-3xl sm:text-5xl text-balance">Klar for bilvask?</h2>
          <Link
            to="/book"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary-strong px-7 py-3.5 text-sm font-medium text-primary-foreground shadow-luxe lift"
          >
            Book time <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
